/**
 * EXT-DEFERRED-PAYOUT(client-page/duck) — data loading for the native deferred-payout
 * checkout. Deliberately thin: it only fetches the listing (with author + images) into
 * the marketplace entities. The actual payment orchestration (create-intent →
 * confirmPayment → paid) lives in the component, because Stripe's PaymentElement is a
 * DOM-mounted, imperative widget rather than Redux state. See DeferredCheckoutPage.js.
 *
 * When the ext is extracted, this duck stays host-specific glue (it speaks the
 * Sharetribe SDK); core/adapters carry no client code.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../../../../util/common';
import { types as sdkTypes, createImageVariantConfig } from '../../../../../util/sdkLoader';
import { storableError } from '../../../../../util/errors';
import { addMarketplaceEntities } from '../../../../../ducks/marketplaceData.duck';

const { UUID } = sdkTypes;

// ================ Async Thunks ================ //

const showListingPayloadCreator = ({ listingId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  return sdk.listings
    .show(params)
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(data, { listingFields }));
      return data;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const showListingThunk = createAsyncThunk(
  'DeferredCheckoutPage/showListing',
  showListingPayloadCreator
);

// ================ Slice ================ //
const initialState = {
  listingId: null,
  showListingInProgress: false,
  showListingError: null,
  // Handed over from ListingPage's OrderPanel submit (handleDeferredSubmit):
  // orderData carries bookingDates / quantity / seats / deliveryMethod / priceVariantName;
  // listing is the denormalised entity (kept for the sessionStorage validity check).
  // The page persists them to sessionStorage via handlePageData, like CheckoutPage.
  orderData: null,
  listing: null,
};

const deferredCheckoutPageSlice = createSlice({
  name: 'DeferredCheckoutPage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.listingId = action.meta.arg.listingId;
        state.showListingError = null;
        state.showListingInProgress = true;
      })
      .addCase(showListingThunk.fulfilled, state => {
        state.showListingInProgress = false;
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        state.showListingError = storableError(action.payload);
        state.showListingInProgress = false;
      });
  },
});

export const { setInitialState, setInitialValues } = deferredCheckoutPageSlice.actions;
export default deferredCheckoutPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  // NOTE: do NOT reset the slice here — setInitialValues (dispatched by
  // handleDeferredSubmit just before navigation) has already replaced the state with
  // this checkout's { listing, orderData }, and loadData runs AFTER it on arrival.
  // A reset would wipe the orderData before the page can read it (the page guards
  // against stale data from a previous listing by matching listing ids instead).
  return dispatch(showListingThunk({ listingId, config }));
};
